"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { initializeTracking, trackPageView } from "@/app/lib/tracking";
import { createLogger } from "@/app/lib/logger";

const log = createLogger("Analytics");

export default function AnalyticsProvider() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastTrackedPath = useRef<string>("");

  // Note: user_role cookie for analytics exclusion is set by middleware (proxy.ts)
  // This ensures the role is available immediately on page load, before tracking fires

  // Initialize tracking on mount (idempotent - safe to call multiple times)
  useEffect(() => {
    initializeTracking().catch((err: unknown) => {
      log.error("Initialization failed", err);
    });
  }, []);

  // Track PageView on route changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    const searchParamsString = searchParams?.toString() || "";
    const currentPath = `${pathname}${searchParamsString ? `?${searchParamsString}` : ""}`;
    if (currentPath === lastTrackedPath.current) return;

    lastTrackedPath.current = currentPath;

    // Small delay to ensure page has rendered
    const t = setTimeout(async () => {
      try {
        await trackPageView({
          page_location: window.location.href,
          page_path: pathname,
          page_title: document.title,
        });
      } catch (error) {
        log.error("PageView tracking failed", error, { page_path: pathname });
      }
    }, 100);

    return () => clearTimeout(t);
  }, [pathname, searchParams]);

  return null;
}
